import { motion } from "framer-motion";
import { Sparkles, Users, Music, Waves, Wind, ArrowLeft, Fish, Droplet } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import { Seo } from "@/components/site/Seo";
import Footer from "@/components/site/Footer";
import FAQ from "@/components/site/FAQ";
import FamilyInWater from "@/components/site/FamilyInWater";
import CommunityBenefits from "@/components/site/CommunityBenefits";
import { ClassLevels } from "@/components/site/ClassLevels";
import WhereNext from "@/components/site/WhereNext";

const BOOK_URL = "https://portal.pivot1st.co.uk/book/2/class";

const toddlerLevels = [
  { icon: <Sparkles size={26} />, name: "Tiny", age: "18 months – 2½ years", note: "Growing curiosity and the first splashes of independence.", accent: "#1F7A8C" },
  { icon: <Fish size={26} />, name: "Tetra", age: "2½ – 3½ years", note: "Playful early lessons building coordination and water confidence.", accent: "#F59E0B" },
  { icon: <Droplet size={26} />, name: "Tots", age: "3½ – 4½ years", note: "Developing independence, breath control and early swimming skills.", accent: "#FF8C6B" },
  { icon: <Waves size={26} />, name: "Baby Sharks", age: "3½ – 4½ years · competency led", note: "Getting ready to swim solo — the bridge to our Sharks pathway. Placement is based on ability, not age.", accent: "#6ED3C3" },
];

const nextItems = [
  { sub: "Where they started", label: "Baby Swimming Lessons", to: "/classes/baby-swimming" },
  { sub: "The next step up", label: "Children's Swimming Lessons", to: "/classes/childrens-swimming" },
  { sub: "Check class availability", label: "Weekly Timetable", to: "/timetable" },
  { sub: "Speak to our team", label: "Contact Us", to: "/contact" },
];

const outcomes = [
  { icon: <Wind size={16} />, text: "Learning through play" },
  { icon: <Sparkles size={16} />, text: "Growing independence" },
  { icon: <Users size={16} />, text: "Small, supportive classes" },
];

const expect = [
  { icon: <Music size={22} />, text: "Songs, games and structured play" },
  { icon: <Waves size={22} />, text: "Floating, kicking and breath control" },
  { icon: <Sparkles size={22} />, text: "Growing independence, step by step" },
  { icon: <Users size={22} />, text: "Small classes with individual attention" },
];

const faqs = [
  {
    q: "Does a parent get in the pool?",
    a: "Yes — adult-accompanied lessons are available for tiddlers up to 4½ years, so a parent or guardian gets in the water with each child. These lessons give the grown-up as much confidence as the child.",
  },
  {
    q: "What should my tiddler wear?",
    a: "For under-3s we require a two-layer swim nappy system: an inner absorbent layer and a snug outer layer (a sealed swim nappy or a wetsuit with a built-in nappy). Once your child is reliably out of nappies, a well-fitting swimsuit is fine. Swim nappies are available at reception.",
  },
  {
    q: "My child is clingy or cries — is that okay?",
    a: "Completely normal, and absolutely okay. We build trust gently and let children settle at their own pace. Many go from clingy to confident within just a few weeks — there's never any pressure.",
  },
  {
    q: "How do you build independence?",
    a: "Through structured play, songs and gentle challenges — floating, kicking and breath control — gradually reducing support as your child's confidence grows, always at a pace that feels right for them.",
  },
  {
    q: "What if my child is ill?",
    a: "Several conditions mean your child shouldn't swim, including diarrhoea, sickness, a heavy cold or an ear infection. Please wait until 48 hours have passed since the last episode of sickness or diarrhoea before returning to the pool.",
  },
];

export default function ToddlerSwimming() {
  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#334155] font-body" data-testid="toddler-swimming-page">
      <Seo
        title="Toddler & Pre-School Swimming Lessons | Happy Tiddlers"
        description="Fun, structured tiddler & pre-school swimming lessons (18 months–4½ years) in Barton-le-Clay, building independence and early swimming skills."
        path="/classes/toddler-swimming"
      />
      <Navbar />

      <section className="relative overflow-hidden pt-14 pb-20 md:pt-20 md:pb-24" data-testid="toddler-header">
        <div className="blob absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#FF7A1A]/10" />
        <div className="container mx-auto px-6 md:px-12 relative">
          <Link to="/classes" className="inline-flex items-center gap-2 text-[#0EA5E9] font-semibold hover:gap-3 transition-all mb-8" data-testid="toddler-back-link">
            <ArrowLeft size={18} /> All classes
          </Link>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <span className="text-sm text-[#FF7A1A] tracking-[0.2em] uppercase font-bold">Tiddler &amp; Pre-School • 18 months – 4 years</span>
              <h1 className="mt-4 font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#0F172A] leading-[1.05]">
                Confident, capable little swimmers
              </h1>
              <p className="mt-6 text-lg text-[#334155] leading-relaxed">
                As tiddlers grow, so does their curiosity in the water. Our pre-school lessons turn
                that curiosity into real skills — through fun, structure and lots of encouragement.
              </p>
              <p className="mt-4 text-lg text-[#334155] leading-relaxed">
                We develop independence, coordination and early swimming skills, one playful step at a time.
              </p>
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" className="inline-block mt-9 bg-[#FF7A1A] text-white font-bold px-8 py-4 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.5)] hover:-translate-y-1 transition-all duration-300" data-testid="toddler-book-cta-top">
                Book tiddler swimming
              </a>
              <div className="mt-8 flex flex-wrap gap-2.5" data-testid="toddler-outcomes">
                {outcomes.map((o) => (
                  <span key={o.text} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-semibold text-[#0F172A] shadow-sm" data-testid="toddler-outcome">
                    <span className="text-[#FF7A1A]">{o.icon}</span>
                    {o.text}
                  </span>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }} className="rounded-[2.5rem] overflow-hidden shadow-2xl shadow-[#0A2540]/20 rotate-2 hover:rotate-0 transition-transform duration-500">
              <img src="https://images.unsplash.com/photo-1638899719048-91b22d3d9ee1" alt="Toddler enjoying a swimming lesson" className="w-full h-[380px] md:h-[520px] object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="relative z-10 -mt-10 md:-mt-16 pb-20 md:pb-24" data-testid="toddler-expect">
        <div className="container mx-auto px-6 md:px-12">
          <div className="bg-white rounded-[2rem] p-8 md:p-12 border border-slate-100 shadow-sm">
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0F172A]">What to expect</h2>
            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              {expect.map((e, i) => (
                <motion.div key={e.text} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="flex items-start gap-4" data-testid={`toddler-expect-${i}`}>
                  <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#FF7A1A] text-white shrink-0">{e.icon}</span>
                  <p className="text-lg text-[#334155] pt-2.5">{e.text}</p>
                </motion.div>
              ))}
            </div>
            <p className="mt-8 text-lg text-[#334155] leading-relaxed border-t border-slate-100 pt-8">
              Every child develops differently — and that's exactly how it should be. We celebrate every small win along the way.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32" data-testid="toddler-focus">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="rounded-[2.5rem] overflow-hidden shadow-xl order-2 lg:order-1">
              <img src="https://images.unsplash.com/photo-1721571388957-0a65fcd4371d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Confident young swimmer in the pool" className="w-full h-[380px] md:h-[460px] object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">
                <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#0EA5E9] text-white"><Wind size={18} /></span>
                Learning through play
              </span>
              <h2 className="mt-5 font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] leading-tight">
                Play is how tiddlers learn best
              </h2>
              <p className="mt-6 text-lg text-[#334155] leading-relaxed">
                Games and songs aren't just fun — they're how we teach floating, kicking and breath control
                without fear or pressure. As trust grows, so does independence, until your little one is
                moving through the water all on their own.
              </p>
              <p className="mt-6 font-heading text-xl font-semibold text-[#0A2540]">Steady progress, one splash at a time.</p>
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" className="inline-block mt-8 bg-[#FF7A1A] text-white font-bold px-8 py-4 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.5)] hover:-translate-y-1 transition-all duration-300" data-testid="toddler-book-cta-bottom">
                Book tiddler swimming
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <FamilyInWater />

      <ClassLevels
        title="The stages your tiddler will grow through"
        intro="Each level builds on the last, moving up gently as your little one gains confidence."
        levels={toddlerLevels}
        accent="#FF7A1A"
      />

      <CommunityBenefits />

      <FAQ items={faqs} intro="Starting pre-school lessons? Here are the questions parents ask us most." />
      <WhereNext items={nextItems} />
      <Footer />
    </div>
  );
}
